import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAdmin } from '../../context/AdminContext';
import { products as defaultProducts } from '../../data/products';
import { textures as defaultTextures } from '../../data/products';
import './FloatingCart.css';

const FloatingCart = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, getCartItemCount, validateAndUpdateCart } = useCart();
  const { products: adminProducts, textures: adminTextures, isInitialized: adminInitialized, refreshData } = useAdmin();
  const [isOpen, setIsOpen] = useState(false);
  const cartRef = useRef(null);

  const products = adminInitialized && adminProducts.length > 0 ? adminProducts : defaultProducts;

  const textures = useMemo(() => {
    if (Array.isArray(adminTextures) && adminTextures.length > 0) {
      const texturesObj = {};
      adminTextures.forEach(texture => {
        texturesObj[texture.id] = { name: texture.name, color: texture.color };
      });
      return { ...defaultTextures, ...texturesObj };
    }
    return defaultTextures;
  }, [adminTextures]);

  const itemCount = getCartItemCount();
  const total = getCartTotal();

  useEffect(() => {
    const validateCart = async () => {
      if (isOpen && adminInitialized) {
        await refreshData();

        setTimeout(() => {
          const currentProducts = adminInitialized && adminProducts.length > 0 ? adminProducts : defaultProducts;
          const currentTextures = adminInitialized && Array.isArray(adminTextures) && adminTextures.length > 0 ? adminTextures : [];
          if (cart.length > 0 && currentProducts.length > 0) {
            validateAndUpdateCart(currentProducts, currentTextures);
          }
        }, 100);
      }
    };

    validateCart();
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cartRef.current && event.target && event.target instanceof Node && !cartRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="floating-cart-container" ref={cartRef}>
      {}
      <button
        className="floating-cart-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Shopping cart"
      >
        <span className="cart-icon-wrapper">
          <svg
            className="cart-icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
        </span>
        {itemCount > 0 && (
          <span className="cart-badge">
            {itemCount}
          </span>
        )}
      </button>
      {}
      {isOpen && (
          <>
            {}
            <div
              className="cart-backdrop"
              onClick={() => setIsOpen(false)}
            />
            {}
            <div
              className="floating-cart-panel"
            >
              <div className="cart-panel-header">
                <h2 className="cart-panel-title">Shopping Cart</h2>

                <button
                  className="cart-panel-close"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close cart"
                >
                  ×
                </button>
              </div>
              {cart.length === 0 ? (
                <div className="cart-empty">
                  <p>Your cart is empty</p>
                  <Link
                    to="/shop"
                    className="cart-shop-link"
                    onClick={() => setIsOpen(false)}
                  >
                    Continue Shopping
                  </Link>
                </div>
              ) : (
                <>
                  <div className="cart-panel-items">
                    {cart.map((item, index) => (
                      <div
                        key={`${item.productId}-${item.texture}-${index}`}
                        className="cart-panel-item"
                      >
                        <img
                          src={item.productImage}
                          alt={item.productName}
                          className="cart-panel-item-image"
                        />
                        <div className="cart-panel-item-info">
                          <h4 className="cart-panel-item-name">{item.productName}</h4>
                          <p className="cart-panel-item-texture">
                            {textures[item.texture]?.name || item.texture}
                          </p>
                          {item.bundleDiscountApplied && (
                            <span className="cart-panel-item-bundle">Color / Style set · {item.bundleDiscountPercent}% off</span>
                          )}
                          <div className="cart-panel-item-controls">
                            <div className="quantity-controls">
                              <button
                                onClick={() => updateQuantity(item.productId, item.texture, item.quantity - 1)}
                                className="quantity-button"
                              >
                                −
                              </button>
                              <span className="quantity-value">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.productId, item.texture, item.quantity + 1)}
                                className="quantity-button"
                              >
                                +
                              </button>
                            </div>
                            <button
                              onClick={() => removeFromCart(item.productId, item.texture)}
                              className="remove-button"
                              aria-label="Remove item"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                        <div className="cart-panel-item-price">
                          ${((parseFloat(item.price) || 0) * item.quantity).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="cart-panel-footer">
                    <div className="cart-panel-total">
                      <span>Total:</span>

                      <span>${total.toFixed(2)}</span>

                    </div>

                    <Link
                      to="/checkout"
                      className="cart-panel-checkout-button"
                      onClick={() => setIsOpen(false)}
                    >
                      Checkout
                    </Link>

                  </div>

                </>

              )}
            </div>

          </>

        )}
    </div>

  );
};

export default FloatingCart;

